<script lang="ts">
	import { downscaleImage } from '#lib/util/downscale-image.ts';
	import { t } from '#lib/i18n/tranlsator.svelte.ts';
	import { Fetchable } from '#lib/util/fetchable';
	import AntonioButtons from './AntonioButtons.svelte';

	interface Props {
		/** Uploads the image to the conversation, then continues the flow. */
		onsubmit: (file: File) => Promise<void>;
		/** No upload: the image is generated from the conversation alone. */
		onskip: () => void;
	}

	const { onsubmit, onskip }: Props = $props();

	let fileInput = $state<HTMLInputElement>();
	let file = $state<File | null>(null);
	const fetchable = new Fetchable();

	// Revoked on replacement/teardown so a visitor cycling through images on a
	// kiosk tablet doesn't leak a blob per attempt.
	let previewUrl = $state<string | null>(null);
	$effect(() => () => {
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
		}
	});

	// Shrunk before it is ever shown or sent, so the preview is exactly what gets
	// uploaded: a phone photo is several MB, and the venue's wifi is the slowest
	// link in the flow. The backend hands the image model a PocketBase thumb
	// regardless, so this is about the upload leg alone.
	async function handleFileChange(e: Event) {
		const selected = (e.currentTarget as HTMLInputElement).files?.[0];
		if (!selected) {
			return;
		}
		fetchable.error = null;
		const prepared = await downscaleImage(selected);
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
		}
		file = prepared;
		previewUrl = URL.createObjectURL(prepared);
	}

	const options = $derived(
		file
			? [
					{
						label: t('antonio.image.use'),
						value: 'use',
						primary: true,
						loading: fetchable.fetching
					},
					{ label: t('antonio.image.change'), value: 'change' }
				]
			: [
					{ label: t('antonio.image.choose'), value: 'change', primary: true },
					{ label: t('antonio.image.skip'), value: 'skip' }
				]
	);

	function handleSelect(value: string) {
		if (value === 'change') {
			fileInput?.click();
			return;
		}
		if (value === 'skip') {
			onskip();
			return;
		}
		if (file) {
			fetchable.fetch(() => onsubmit(file!));
		}
	}
</script>

<div class="antonio-image-upload">
	<div class="panel">
		{#if previewUrl}
			<img class="preview" src={previewUrl} alt="" />
		{/if}
		<p class="hint">{t('antonio.image.hint')}</p>
		{#if fetchable.error}
			<p class="error">{t('antonio.image.uploadError')}</p>
		{/if}
		<AntonioButtons {options} onselect={handleSelect} disabled={fetchable.fetching} />
	</div>
	<input
		bind:this={fileInput}
		type="file"
		accept="image/*"
		capture="user"
		onchange={handleFileChange}
		hidden
	/>
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.antonio-image-upload {
		@include lib.flex-column();
		gap: 0.5rem;
		padding: 0.5rem 0;

		.panel {
			@include lib.flex-column();
			gap: 0.5rem;
			padding: 0.5rem;
			border: 1px solid var(--border-subtle);
			border-radius: 0.75rem;

			.preview {
				align-self: center;
				width: 8rem;
				aspect-ratio: 1;
				object-fit: cover;
				border-radius: 0.75rem;
			}

			.hint {
				font-size: 0.875rem;
				color: var(--text-secondary);
				text-align: center;
			}

			.error {
				font-size: 0.875rem;
				color: var(--text-error);
				text-align: center;
			}
		}
	}
</style>
